import { toast } from 'sonner'
import type { CSSProperties } from 'react'

/**
 * Cores disponíveis para toast (similar ao badge)
 */
type ToastColor = 'success' | 'error' | 'warning' | 'info'

/**
 * Helper para mostrar toast com cores personalizadas
 */
export function showToast(
  message: string,
  color?: ToastColor,
  options?: {
    duration?: number
    action?: {
      label: string
      onClick: () => void
    }
  },
) {
  const baseOptions = {
    duration: options?.duration || 4000,
    ...options,
  }

  // Define cores baseado no tipo (estilo sutil com fundo claro e texto escuro, similar ao badge subtle)
  const colorStyles: Record<ToastColor, CSSProperties> = {
    success: {
      backgroundColor: '#bbf7d0', // green-200
      color: '#166534', // green-800
      borderColor: '#86efac', // green-300
    },
    error: {
      backgroundColor: '#fecaca', // red-200
      color: '#991b1b', // red-800
      borderColor: '#fca5a5', // red-300
    },
    warning: {
      backgroundColor: '#fef08a', // yellow-200
      color: '#854d0e', // yellow-800
      borderColor: '#fde047', // yellow-300
    },
    info: {
      backgroundColor: '#bfdbfe', // blue-200
      color: '#1e3a8a', // blue-800
      borderColor: '#93c5fd', // blue-300
    },
  }

  switch (color) {
    case 'success':
      return toast.success(message, {
        ...baseOptions,
        style: colorStyles.success,
      })
    case 'error':
      return toast.error(message, {
        ...baseOptions,
        style: colorStyles.error,
      })
    case 'warning':
      return toast.warning(message, {
        ...baseOptions,
        style: colorStyles.warning,
      })
    case 'info':
      return toast.info(message, {
        ...baseOptions,
        style: colorStyles.info,
      })
    default:
      return toast(message, baseOptions)
  }
}

/**
 * Toast de sucesso
 */
export function toastSuccess(message: string, duration?: number) {
  return showToast(message, 'success', { duration })
}

/**
 * Toast de erro
 */
export function toastError(message: string, duration?: number) {
  return showToast(message, 'error', { duration })
}

/**
 * Toast de aviso
 */
export function toastWarning(message: string, duration?: number) {
  return showToast(message, 'warning', { duration })
}

/**
 * Toast de informação
 */
export function toastInfo(message: string, duration?: number) {
  return showToast(message, 'info', { duration })
}
